import { SimpleToggle } from "components/Form/components/Switch/Switch";
import { requiredValidator } from "components/Form/Form.utils";
import { IconCollection } from "components/Icon/IconCollection";
import RecordLabel from "components/RecordLabel";
import { TimeAgoWithDateTimeFormatter } from "components/TimeAgo/TimeAgoWithDateTimeFormatter";
import { ResourceConfig } from "config/config";
import { executionLocationField } from "config/resources/sharedFieldsTyped";
import {
    DeleteListAction,
    PermanentlyDeleteListAction,
    deleteActionSignature,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    moveToLocationActionSignature,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    commentsTab,
    logsTab,
    logsTabKey,
    metadataTab,
    referencesUsedByTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { TriggerStampButton } from "containers/Resources/screens/ResourcesSingle/components/TriggerStampButton";
import {
    buildSimpleColumn,
    isEnabledColumn,
} from "containers/Search/components/SearchTabs/columns";
import { getRelativeUrl } from "utils/service";
import identity from "./identity";
import resource from "./resource";
import { getLatestExecutions, getUrlDTO } from "./sharedFields";

interface WebhookSecurityProps {
    is_enabled: boolean;
    require_login: boolean;
    is_key_required: boolean;
}

const resourceName = "webhook";

/**
 * Tri-state "cancel on abandon" options. The stored column
 * `webhook_cancel_enabled` is a nullable boolean (null = inherit the global
 * WEBHOOK_CANCEL_ENABLED, true = always cancel, false = never cancel); the form
 * carries it as one of these string tokens and maps back to boolean|null on
 * submit (see the field's submitDto and formDTO.initialValueDTO below).
 */
const CANCEL_ON_ABANDON_INHERIT = { value: "inherit", label: "Inherit workspace default" };
const cancelOnAbandonOptions = [
    CANCEL_ON_ABANDON_INHERIT,
    { value: "always", label: "Always cancel" },
    { value: "never", label: "Never cancel" },
];

/**
 * Per-field "Stamp input schema" action rendered next to the webhook's flow
 * picker. Resolved at render time by ResourceEditFields, which supplies the
 * saved record id + a refetch callback. Mirrors object_template's
 * hookStampActionButtons; posts to POST /webhook/{id}/stamp_input_schema.
 */
const webhookStampActionButtons = (
    currentFormValues: any,
    ctx: { recordId?: string; fetchResourceData?: (() => any) | null; isDisabled?: boolean }
) => (
    <TriggerStampButton
        triggerKind="webhook"
        triggerId={ctx.recordId}
        flowSelected={!!currentFormValues?.flow_id}
        disabled={ctx.isDisabled}
        fetchResourceData={ctx.fetchResourceData}
    />
);

const config: ResourceConfig = {
    label: "Webhook",
    icon: IconCollection.webhook,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: logsTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [logsTab.tab, commentsTab.tab, referencesUsedByTab.tab, metadataTab.tab],
                content: [logsTab.content, commentsTab.content, referencesUsedByTab.content, metadataTab.content],
            },
        },
    ],
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            ${identity.graphql.resourceBody}
            flow_id
            flow_id_flow {
                id
                name
            }
            require_login
            is_key_required
            url
            is_enabled
            is_productive
            last_activity
            execution_location
            webhook_cancel_enabled
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            is_enabled
            is_productive
            flow_id_flow {
                id
                name
            }
            execution_location
            url
            is_key_required
            require_login
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $flow_id: String,
            $is_productive: Boolean,
            $execution_location: String,
            $webhook_cancel_enabled: Boolean,
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            flow_id: $flow_id,
            is_productive: $is_productive,
            execution_location: $execution_location,
            webhook_cancel_enabled: $webhook_cancel_enabled,
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $flow_id: String,
            $is_productive: Boolean,
            $execution_location: String,
            $webhook_cancel_enabled: Boolean,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            flow_id: $flow_id,
            is_productive: $is_productive,
            execution_location: $execution_location,
            webhook_cancel_enabled: $webhook_cancel_enabled,
        `,
    },
    actions: {
        shown: [],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    additionalColumns: (setNewValue) => [
        isEnabledColumn(setNewValue),
        {
            label: "Productive",
            key: "is_productive",
            shown: true,
            sortable: true,
            dto: (value: any) =>
                buildSimpleColumn(
                    <SimpleToggle
                        identifier="is_productive"
                        value={value.is_productive}
                        isReadOnly={true}
                    />,
                    setNewValue
                        ? () => setNewValue({ field: "is_productive", value: value.is_productive })
                        : undefined
                ),
        },
        {
            label: "Flow",
            key: "flow",
            shown: true,
            dto: ({ flow_id_flow }: any) => {
                return (
                    flow_id_flow && (
                        <RecordLabel
                            link={getRelativeUrl("flow", flow_id_flow.id)}
                            name={flow_id_flow.name}
                            iconByType="flow"
                        />
                    )
                );
            },
        },
        {
            label: "Execution location",
            key: "execution_location",
            shown: false,
            sortable: true,
            dto: (value) =>
                buildSimpleColumn(
                    value?.execution_location,
                    setNewValue
                        ? () =>
                              setNewValue({
                                  field: "execution_location",
                                  value: value.execution_location,
                              })
                        : undefined
                ),
        },
        {
            label: "Url",
            key: "url",
            shown: false,
            sortable: false,
            dto: (value) =>
                buildSimpleColumn(
                    getUrlDTO(value?.url),
                    setNewValue ? () => setNewValue({ field: "url", value: value.url }) : undefined
                ),
        },
        {
            label: "Is key required",
            key: "is_key_required",
            shown: false,
            sortable: true,
            dto: (value: any) =>
                buildSimpleColumn(
                    <SimpleToggle
                        identifier="is_key_required"
                        value={value.is_key_required}
                        isReadOnly={true}
                    />,
                    setNewValue
                        ? () =>
                              setNewValue({
                                  field: "is_key_required",
                                  value: value.is_key_required,
                              })
                        : undefined
                ),
        },
        {
            label: "Require login",
            key: "require_login",
            shown: false,
            sortable: true,
            dto: (value: any) =>
                buildSimpleColumn(
                    <SimpleToggle
                        identifier="require_login"
                        value={value.require_login}
                        isReadOnly={true}
                    />,
                    setNewValue
                        ? () => setNewValue({ field: "require_login", value: value.require_login })
                        : undefined
                ),
        },
    ],
    statsSidebar: [getLatestExecutions("webhook")],
    form: [
        {
            name: "flow_id",
            component: "autocomplete",
            fieldProps: {
                label: "Flow",
                options: {
                    resource: "flow",
                },
                docs: {
                    text: "Choose a flow to call on this webhook",
                    urlPath: "/docs/webhooks#configuration",
                },
                getActionButtons: webhookStampActionButtons,
            },
            fieldAttr: {
                required: true,
            },
            validation: requiredValidator,
        },
        {
            name: "webhookSecurity",
            component: "webhookSecurity",
            fieldProps: {},
        },
        {
            name: "is_productive",
            component: "switch",
            fieldProps: {
                label: "Is productive",
                docs: {
                    text: "Does this webhook work in production",
                    urlPath: "/docs/webhooks#configuration",
                },
                defaultValue: true,
            },
        },
        {
            name: "webhook_cancel_enabled",
            component: "autocomplete",
            fieldProps: {
                label: "Cancel on abandon",
                options: {
                    list: cancelOnAbandonOptions,
                },
                clearable: false,
                defaultValue: CANCEL_ON_ABANDON_INHERIT,
                docs: {
                    text:
                        "Whether abandoning a synchronous webhook call cancels the " +
                        "execution it started (async calls are never cancelled)",
                    urlPath: "/docs/webhooks#cancelling-the-execution-when-the-caller-goes-away",
                },
            },
            submitDto: (value: any) =>
                value === "always" ? true : value === "never" ? false : null,
        },
        {
            name: "url",
            component: "url",
            fieldProps: {
                label: "Url",
                asLink: true,
            },
        },
        executionLocationField({
            optionList: [
                { value: "flow_record", label: "Flow" },
                { value: "webhook_record", label: "Webhook" },
            ],
            docs: {
                text: "Where the executions started by this webhook will run",
                urlPath: "/docs/webhooks#configuration",
            },
        }),
    ],
    formDTO: {
        initialValueDTO: (resourceData: any) => {
            const {
                is_enabled,
                is_key_required,
                require_login,
                webhook_cancel_enabled,
                ...rest
            } = resourceData ?? {};
            const webhookSecurity: WebhookSecurityProps = {
                is_enabled,
                is_key_required,
                require_login,
            };
            // nullable boolean column -> tri-state string token for the form
            const cancelOnAbandon =
                webhook_cancel_enabled === true
                    ? "always"
                    : webhook_cancel_enabled === false
                      ? "never"
                      : "inherit";

            return { ...rest, webhookSecurity, webhook_cancel_enabled: cancelOnAbandon };
        },
    },
    detailsSidebar: [
        {
            label: "Last activity",
            valueKey: "last_activity",
            dto: (value: any) => (value ? <TimeAgoWithDateTimeFormatter datetime={value} /> : null),
        },
    ],
};
export default config;
